import { useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import type { AppWorkOrderDetail } from '../data/apiContract.ts';
import { formatScheduleDateLabel, jobStatusClass } from '../utils';
import type { DemoSnapshot, FlowState, TechnicianLane, WorkOrder } from '../data/types';

type BackendJob = {
  time: string;
  title: string;
  client: string;
  status: string;
  workOrderId: string;
};

type BackendLane = {
  name: string;
  zone: string;
  utilization: string;
  jobs: BackendJob[];
};

const UNASSIGNED_FILTER_VALUE = '__unassigned__';

function toLocalDateKey(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function formatTimeLabel(value: string | null | undefined): string {
  if (!value) return 'À planifier';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function getDispatchStatusLabel(status: string): string {
  switch (status) {
    case 'scheduled':
      return 'Planifiée';
    case 'on_site':
      return 'Sur site';
    case 'in_review':
      return 'En validation';
    case 'rework_required':
      return 'Reprise requise';
    case 'ready_to_invoice':
      return 'Prêt à facturer';
    case 'invoiced':
      return 'Facturé';
    default:
      return status;
  }
}

function groupBackendWorkOrders(
  workOrders: AppWorkOrderDetail[],
  selectedScheduleDate: Date,
  technicianFilter: string,
): { lanes: BackendLane[]; unassigned: AppWorkOrderDetail[]; technicianOptions: string[] } {
  const selectedDateKey = toLocalDateKey(selectedScheduleDate);
  const technicianNames = new Set<string>();
  const filteredWorkOrders = workOrders
    .filter((order) => Boolean(order.scheduledStart))
    .filter((order) => {
      if (!selectedDateKey) return true;
      return toLocalDateKey(order.scheduledStart) === selectedDateKey;
    })
    .filter((order) => {
      if (technicianFilter === 'all') return true;
      if (technicianFilter === UNASSIGNED_FILTER_VALUE) return !order.technician?.displayName;
      return order.technician?.displayName === technicianFilter;
    })
    .sort((left, right) => (left.scheduledStart ?? '').localeCompare(right.scheduledStart ?? ''));

  const grouped = new Map<string, BackendLane>();
  const unassigned: AppWorkOrderDetail[] = [];

  for (const order of filteredWorkOrders) {
    const techName = order.technician?.displayName?.trim() || 'Non assigné';
    const zone = order.technician?.zone?.trim() || 'Affectation en attente';
    const time = formatTimeLabel(order.scheduledStart);
    const job: BackendJob = {
      time,
      title: order.title,
      client: order.client.name,
      status: getDispatchStatusLabel(order.status),
      workOrderId: order.id,
    };

    if (!order.technician?.displayName) {
      unassigned.push(order);
    }

    technicianNames.add(techName);
    const lane = grouped.get(techName) ?? {
      name: techName,
      zone,
      utilization: '',
      jobs: [],
    };
    lane.zone = zone;
    lane.jobs.push(job);
    grouped.set(techName, lane);
  }

  const lanes = [...grouped.values()]
    .map((lane) => ({
      ...lane,
      jobs: lane.jobs.sort((left, right) => left.time.localeCompare(right.time)),
      utilization: `${lane.jobs.length} intervention${lane.jobs.length > 1 ? 's' : ''}`,
    }))
    .sort((left, right) => left.name.localeCompare(right.name));

  const technicianOptions = [...technicianNames].sort((left, right) => left.localeCompare(right));
  if (workOrders.some((order) => !order.technician?.displayName)) {
    technicianOptions.push(UNASSIGNED_FILTER_VALUE);
  }

  return { lanes, unassigned, technicianOptions };
}

export function DispatchPage(props: {
  lanes: TechnicianLane[];
  signals: DemoSnapshot['dispatchSignals'];
  unassignedJobs: DemoSnapshot['unassignedJobs'];
  backendWorkOrders: AppWorkOrderDetail[];
  backendWorkOrdersLoading: boolean;
  backendWorkOrdersEnabled: boolean;
  focusOrder: WorkOrder | null;
  flowState: FlowState;
  dispatchOwnerName: string;
  dispatchScheduledWindow: string;
  dispatchTechnicianOptions: string[];
  dispatchWindowOptions: string[];
  onChangeDispatchOwnerName: (value: string) => void;
  onChangeDispatchScheduledWindow: (value: string) => void;
  onConfirmDispatch: () => Promise<void>;
  onOpenQuote: () => Promise<void>;
  onOpenWorkOrderByTitle: (title: string) => void;
  canManageDispatch: boolean;
}) {
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(() => new Date());
  const [technicianFilter, setTechnicianFilter] = useState('all');

  const scheduleDateLabel = formatScheduleDateLabel(selectedScheduleDate);
  const useBackendDayView = props.backendWorkOrdersEnabled;
  const backendView = useMemo(
    () => groupBackendWorkOrders(props.backendWorkOrders, selectedScheduleDate, technicianFilter),
    [props.backendWorkOrders, selectedScheduleDate, technicianFilter],
  );
  const legacyTechnicianNames = useMemo(() => props.lanes.map((lane) => lane.name), [props.lanes]);
  const legacyVisibleLanes = useMemo(
    () => (technicianFilter === 'all' ? props.lanes : props.lanes.filter((lane) => lane.name === technicianFilter)),
    [props.lanes, technicianFilter],
  );

  const moveScheduleDate = (deltaDays: number) => {
    setSelectedScheduleDate((current) => {
      const next = new Date(current);
      next.setDate(next.getDate() + deltaDays);
      return next;
    });
  };

  return (
    <div className="page-stack">
      <div className="mini-metric-grid">
        {props.signals.map((item) => (
          <article key={item.label} className="mini-metric">
            <span className="mini-metric__label">{item.label}</span>
            <strong>{item.value}</strong>
            <span>{item.detail}</span>
          </article>
        ))}
      </div>

      <div className="dispatch-toolbar">
        <div className="dispatch-toolbar__date-nav">
          <button type="button" className="secondary-button" onClick={() => moveScheduleDate(-1)}>
            Jour précédent
          </button>
          <div className="dispatch-toolbar__date-label">
            <strong>{scheduleDateLabel}</strong>
            <span>Planning journalier avec navigation rapide et filtre d’équipe.</span>
          </div>
          <button type="button" className="secondary-button" onClick={() => moveScheduleDate(1)}>
            Jour suivant
          </button>
          <button type="button" className="primary-button primary-button--compact" onClick={() => setSelectedScheduleDate(new Date())}>
            Aujourd'hui
          </button>
        </div>
        <label className="field-stack dispatch-toolbar__filter">
          <span>Technicien</span>
          <select value={technicianFilter} onChange={(event) => setTechnicianFilter(event.target.value)}>
            <option value="all">Tous les techniciens</option>
            {(useBackendDayView ? backendView.technicianOptions : legacyTechnicianNames).map((technicianName) => (
              <option key={technicianName} value={technicianName}>
                {technicianName === UNASSIGNED_FILTER_VALUE ? 'Sans affectation' : technicianName}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="dispatch-layout">
        <div className="lane-board">
          <Card
            title={props.focusOrder ? `Affectation ${props.focusOrder.id}` : 'Affectation à planifier'}
            subtitle={
              props.focusOrder
                ? `${props.focusOrder.title} · ${props.focusOrder.client}`
                : 'Le devis doit être converti avant affectation terrain.'
            }
          >
            {props.flowState.quoteConverted && props.focusOrder ? (
              <div className="dispatch-editor">
                <div className="dispatch-editor__summary">
                  <div>
                    <span className="detail-card__label">OT</span>
                    <strong>{props.focusOrder.id}</strong>
                    <span>{props.focusOrder.status}</span>
                  </div>
                  <div>
                    <span className="detail-card__label">Site</span>
                    <strong>{props.focusOrder.site}</strong>
                    <span>{props.focusOrder.revenue}</span>
                  </div>
                </div>
                <div className="dispatch-editor__form">
                  <label className="field-stack">
                    <span>Technicien</span>
                    <select
                      value={props.dispatchOwnerName}
                      onChange={(event) => props.onChangeDispatchOwnerName(event.target.value)}
                      disabled={!props.canManageDispatch}
                    >
                      {props.dispatchTechnicianOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field-stack">
                    <span>Créneau</span>
                    <select
                      value={props.dispatchScheduledWindow}
                      onChange={(event) => props.onChangeDispatchScheduledWindow(event.target.value)}
                      disabled={!props.canManageDispatch}
                    >
                      {props.dispatchWindowOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="action-row">
                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => void props.onConfirmDispatch()}
                    disabled={!props.canManageDispatch}
                  >
                    Confirmer l'affectation
                  </button>
                </div>
              </div>
            ) : (
              <div className="dispatch-editor dispatch-editor--empty">
                <p>Convertir le devis sélectionné pour ouvrir l'affectation terrain.</p>
                <div className="action-row">
                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => void props.onOpenQuote()}
                    disabled={!props.canManageDispatch}
                  >
                    Convertir depuis le devis
                  </button>
                </div>
              </div>
            )}
          </Card>

          {useBackendDayView ? (
            <>
              {props.backendWorkOrdersLoading ? (
                <Card title="Planning du jour" subtitle="Lecture des interventions planifiées depuis l’API.">
                  <div className="app-empty">
                    <strong>Chargement</strong>
                    <span>Lecture du planning backend en cours.</span>
                  </div>
                </Card>
              ) : backendView.lanes.length ? (
                backendView.lanes.map((lane) => (
                  <Card key={lane.name} title={lane.name} subtitle={`${lane.zone} · ${lane.utilization}`}>
                    <div className="job-lane">
                      {lane.jobs.map((job) => (
                        <button
                          key={`${lane.name}-${job.workOrderId}-${job.time}`}
                          type="button"
                          className={`job-pill job-pill--${jobStatusClass(job.status)} job-pill--interactive`}
                          onClick={() => props.onOpenWorkOrderByTitle(job.title)}
                        >
                          <div className="job-pill__time">{job.time}</div>
                          <div>
                            <strong>{job.title}</strong>
                            <span>{job.client}</span>
                          </div>
                          <em>{job.status}</em>
                        </button>
                      ))}
                    </div>
                  </Card>
                ))
              ) : (
                <Card title="Planning du jour" subtitle="Lecture des interventions planifiées depuis l’API.">
                  <div className="app-empty">
                    <strong>Aucun OT planifié ce jour</strong>
                    <span>Changer le jour ou le technicien pour voir une autre tranche du planning.</span>
                  </div>
                </Card>
              )}
            </>
          ) : (
            legacyVisibleLanes.map((lane) => (
              <Card key={lane.name} title={lane.name} subtitle={`${lane.zone} · ${lane.utilization}`}>
                <div className="job-lane">
                  {lane.jobs.map((job) => (
                    <button
                      key={`${lane.name}-${job.time}-${job.title}`}
                      type="button"
                      className={`job-pill job-pill--${jobStatusClass(job.status)} job-pill--interactive`}
                      onClick={() => props.onOpenWorkOrderByTitle(job.title)}
                    >
                      <div className="job-pill__time">{job.time}</div>
                      <div>
                        <strong>{job.title}</strong>
                        <span>{job.client}</span>
                      </div>
                      <em>{job.status}</em>
                    </button>
                  ))}
                </div>
              </Card>
            ))
          )}
        </div>

        <Card
          title={useBackendDayView ? 'File non affectée du jour' : 'File non affectée'}
          subtitle="Les interventions qui nécessitent arbitrage ou compétence rare."
        >
          <div className="queue-list">
            {useBackendDayView ? (
              props.backendWorkOrdersLoading ? (
                <div className="app-empty">
                  <strong>Chargement</strong>
                  <span>Lecture de la file non affectée en cours.</span>
                </div>
              ) : backendView.unassigned.length ? (
                backendView.unassigned.map((job) => (
                  <button
                    key={job.id}
                    type="button"
                    className="queue-item queue-item--interactive"
                    onClick={() => props.onOpenWorkOrderByTitle(job.title)}
                  >
                    <strong>{job.title}</strong>
                    <span>{job.site.addressLine}</span>
                    <div className="queue-item__meta">
                      <span>{formatTimeLabel(job.scheduledStart)}</span>
                      <span>{job.technician?.displayName ?? 'Sans affectation'}</span>
                    </div>
                  </button>
                ))
              ) : (
                <div className="app-empty">
                  <strong>Aucune intervention sans affectation</strong>
                  <span>Le planning backend du jour est entièrement pourvu.</span>
                </div>
              )
            ) : (
              props.unassignedJobs.map((job) => (
                <button
                  key={job.title}
                  type="button"
                  className="queue-item queue-item--interactive"
                  onClick={() => props.onOpenWorkOrderByTitle(job.title)}
                >
                  <strong>{job.title}</strong>
                  <span>{job.reason}</span>
                  <div className="queue-item__meta">
                    <span>{job.sla}</span>
                    <span>{job.owner}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
